import { useEffect, useState } from "react";
import { Inbox, ArrowRight, Eraser } from "lucide-react";
import { Progress } from "./ui/progress";
import { Button } from "./ui/button";
import { ResetDialog } from "./ResetDialog";
import { api } from "../lib/api";
import { PrintButtons } from "./PrintDialog";
import { fmtEUR } from "../lib/format";

const Stat = ({ label, value, cls = "text-slate-900", testId }) => (
  <div>
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`font-num mt-0.5 text-xl font-semibold ${cls}`} data-testid={testId}>{value}</div>
  </div>
);

export default function Overview({ refreshKey, onOpenYear, onOpenInbox, onChanged }) {
  const [data, setData] = useState(null);
  const [resetYear, setResetYear] = useState(null);
  useEffect(() => { api.overview().then(setData); }, [refreshKey]);
  if (!data) return null;
  return (
    <div className="grid gap-6 p-4 sm:p-6 lg:p-8 fade-up" data-testid="overview-view">
      <div>
        <div className="text-sm text-slate-500">Übersicht</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Wie weit sind Ihre Belege?</h1>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {data.years.map((y) => {
          const pct = y.total ? Math.round((y.done / y.total) * 100) : 0;
          return (
            <button key={y.year} onClick={() => onOpenYear(y.year)} data-testid={`overview-year-${y.year}`}
              className="group rounded-xl border border-slate-200 bg-white p-5 text-left transition-colors duration-150 hover:border-slate-400">
              <div className="flex items-center justify-between">
                <span className="font-display text-2xl font-bold text-slate-900">{y.year}</span>
                <span className="flex items-center gap-1 text-sm text-slate-500 group-hover:text-slate-900">{y.statements} Auszug{y.statements !== 1 ? "e" : ""}<ArrowRight className="h-4 w-4" /></span>
              </div>
              <Progress value={pct} className="mt-4 h-2.5" />
              <div className="mt-1 text-sm text-slate-600"><span className="font-num font-semibold text-slate-900">{pct} %</span> erledigt</div>
              <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                <Stat label="Gesamt" value={y.total} testId={`overview-total-${y.year}`} />
                <Stat label="Erledigt" value={y.done} cls="text-emerald-700" testId={`overview-done-${y.year}`} />
                <Stat label="Offen" value={y.open} cls={y.open ? "text-amber-700" : "text-slate-900"} testId={`overview-open-${y.year}`} />
                <Stat label="Offener Betrag" value={fmtEUR(y.open_amount)} testId={`overview-open-amount-${y.year}`} />
              </div>
            </button>
          );
        })}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {data.years.map((y) => (
          <div key={y.year} className="grid gap-2">
            <PrintButtons scope={{ year: y.year }} label={`Jahr ${y.year}`} testPrefix={`year-${y.year}`} />
            <Button variant="ghost" size="sm" className="w-fit gap-1.5 text-amber-800 hover:bg-amber-50" data-testid={`reset-year-btn-${y.year}`} onClick={() => setResetYear(y.year)}>
              <Eraser className="h-4 w-4" /> Jahr {y.year} zurücksetzen
            </Button>
          </div>
        ))}
      </div>
      <ResetDialog open={resetYear !== null} onOpenChange={(o) => !o && setResetYear(null)} testPrefix="year"
        title={`Jahr ${resetYear} zurücksetzen?`} confirmWord={String(resetYear)}
        run={(dry, confirm) => api.resetYear(resetYear, dry, confirm)} onDone={() => { onChanged?.(); api.overview().then(setData); }} />
      <button onClick={onOpenInbox} data-testid="overview-inbox-card" className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5 text-left hover:border-slate-400 lg:w-1/2">
        <Inbox className="h-8 w-8 text-slate-400" />
        <div className="flex-1">
          <div className="font-semibold text-slate-900">Belege-Eingang</div>
          <div className="text-sm text-slate-600"><span className="font-num font-semibold">{data.receipts_unassigned}</span> nicht zugeordnet · <span className="font-num">{data.receipts_total}</span> Belege gesamt</div>
        </div>
        <ArrowRight className="h-5 w-5 text-slate-400" />
      </button>
    </div>
  );
}
